import React, { useCallback } from 'react';
import { type OptionsWithExtraProps, type SnackbarKey, type SnackbarMessage, useSnackbar, type VariantType } from 'notistack';
import { prop } from 'ramda';

import { useTranslations } from '@core/js/hooks/i18n';
import { useUserSelector } from '@core/js/store/hooks';

import style from './Notifications.scss';

interface SnackbarOptionsWithExtendedProps
    extends OptionsWithExtraProps<VariantType> {
    // hc-changes - extension of the toastOptions object with our Props
    Icon?: null;
    tKeyTitle?: string;
    title?: string;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    handleClose?: () => void;
    isHideCloseBtn?: boolean;
}

const DEFAULT_OPTIONS = {
    hideIconVariant: true,
    disableWindowBlurListener: true,
    SnackbarProps: {
        'aria-live': 'polite'
    } as React.HTMLAttributes<HTMLDivElement>
};

export const useShowNotification = (options?: OptionsWithExtraProps<VariantType>) => {
    const translate = useTranslations();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const toastTimeoutValue = useUserSelector(prop('toastTimeoutValue'));

    return useCallback((
        messageOrTKey: SnackbarMessage,
        toastOptions?: SnackbarOptionsWithExtendedProps
    ): SnackbarKey => {
        const snackbarOptions: SnackbarOptionsWithExtendedProps = {
            ...DEFAULT_OPTIONS,
            autoHideDuration: toastTimeoutValue,
            ...window.Playtech.API.common.notificationsOptions || {},
            anchorOrigin: { horizontal: 'right', vertical: 'top' }
        };

        if (options) {
            Object.assign(snackbarOptions, options);
        }

        if (toastOptions) {
            Object.assign(snackbarOptions, toastOptions);
        }

        const {
            onClick,
            handleClose,
            isHideCloseBtn = true
        } = snackbarOptions || {};
        snackbarOptions.className = `${style.notification} ${style[snackbarOptions.variant || 'default']}`;
        snackbarOptions.action = (snackbarId: SnackbarKey) => (
            <>
                {/* hc-changes - start - onClickContainer */}
                {onClick && (
                    <span
                        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                            onClick(e);
                            closeSnackbar(snackbarId);
                        }}
                        className={style.onClickContainer}
                    />
                )}

                {!isHideCloseBtn && (
                    <div
                        onClick={() => {
                            handleClose?.();
                            return closeSnackbar(snackbarId);
                        }}
                        className={style.closeButton}
                    >{translate('playtech.mobile.system.notification.close')}</div>
                )}
                {/* hc-changes - end */}
            </>
        );

        const content = typeof messageOrTKey === 'string' && messageOrTKey.startsWith('playtech.mobile.')
            ? translate(messageOrTKey)
            : messageOrTKey;

        const message = typeof content === 'string'
            ? <span dangerouslySetInnerHTML={{ __html: content }} />
            : content;
        const snackbarOptionsParsed = {
            ...snackbarOptions,
            ...snackbarOptions.autoHideDuration && { autoHideDuration: snackbarOptions.autoHideDuration * 1000 }
        };

        return enqueueSnackbar(message, snackbarOptionsParsed);
    }, [options, translate, enqueueSnackbar, closeSnackbar, toastTimeoutValue]);
};
